import './NewFortuneWheelModal.scss';
import { observer } from 'mobx-react-lite';
import { authStore, fortuneWheelStore } from '../../../../../platform/stores';
import { useEffect, useState } from 'react';

export const NewFortuneWheelModal = observer(() => {
  const [isClosing, setIsClosing] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  const wheelData = fortuneWheelStore.wheelData;

  useEffect(() => {
    if (wheelData) {
      if (fortuneWheelStore.forceShowNewFortuneWheelModal) {
        setIsVisible(true);
        return;
      }
      // Check if game loading is complete
      const isGameLoaded = fortuneWheelStore.gameLoadingComplete;

      // Check if user has chosen to not show this fortune wheel notification
      const hiddenPromotions = JSON.parse(localStorage.getItem('hiddenFortuneWheelNotifications') || '[]');
      const shouldShow = !hiddenPromotions.includes(wheelData.id);

      // Don't show if the wheel modal is already open (user is spinning)
      const isWheelModalOpen = fortuneWheelStore.dailyWheelModalIsOpen;

      // Don't show if user has already spun (has lastSpinDate) - they've already participated
      const hasSpun = !!fortuneWheelStore.lastSpinDate;

      // Don't show if modal has already been shown/closed in this session
      const hasBeenShown = fortuneWheelStore.newFortuneWheelModalShown;

      // Show promotional modal only if: game is loaded, user hasn't dismissed it, wheel modal isn't open, user hasn't spun yet, and modal hasn't been shown yet
      if (isGameLoaded && shouldShow && !isWheelModalOpen && !hasSpun && !hasBeenShown) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    } else {
      setIsVisible(false);
    }
  }, [
    wheelData,
    fortuneWheelStore.forceShowNewFortuneWheelModal,
    fortuneWheelStore.gameLoadingComplete,
    fortuneWheelStore.dailyWheelModalIsOpen,
    fortuneWheelStore.lastSpinDate,
    fortuneWheelStore.newFortuneWheelModalShown
  ]);

  if (!wheelData || !isVisible) {
    return null;
  }

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      setIsVisible(false);

      // Mark modal as shown so it doesn't appear again in this session
      fortuneWheelStore.setNewFortuneWheelModalShown(true);
      fortuneWheelStore.setForceShowNewFortuneWheelModal(false);

      // Save to localStorage if user chose "don't show again"
      if (dontShowAgain) {
        const hiddenPromotions = JSON.parse(localStorage.getItem('hiddenFortuneWheelNotifications') || '[]');
        if (!hiddenPromotions.includes(wheelData.id)) {
          hiddenPromotions.push(wheelData.id);
          localStorage.setItem('hiddenFortuneWheelNotifications', JSON.stringify(hiddenPromotions));
        }
      }
    }, 300);
  };

  const handlePlayNow = () => {
    if (!wheelData) return;

    // Store wheel ID before state changes
    const wheelId = wheelData.id;

    // Close this promotional modal
    setIsClosing(true);
    setIsVisible(false);

    // Mark modal as shown so it doesn't appear again in this session
    fortuneWheelStore.setNewFortuneWheelModalShown(true);
    fortuneWheelStore.setForceShowNewFortuneWheelModal(false);

    // Save to localStorage if user chose "don't show again"
    if (dontShowAgain) {
      const hiddenPromotions = JSON.parse(localStorage.getItem('hiddenFortuneWheelNotifications') || '[]');
      if (!hiddenPromotions.includes(wheelId)) {
        hiddenPromotions.push(wheelId);
        localStorage.setItem('hiddenFortuneWheelNotifications', JSON.stringify(hiddenPromotions));
      }
    }

    // Open the actual wheel modal after a short delay for smooth transition
    setTimeout(() => {
      fortuneWheelStore.setDailyWheelModalIsOpen(true);
      setIsClosing(false);
    }, 300);
  };

  // Format participation period (24 hours is 86400 seconds)
  const formatParticipationPeriod = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    if (hours === 24) {
      return '24 hours';
    } else if (hours > 0) {
      return `${hours} hour${hours > 1 ? 's' : ''}`;
    }
    const minutes = Math.floor(seconds / 60);
    return `${minutes} minute${minutes > 1 ? 's' : ''}`;
  };

  // Get participation period from configuration (preferred) or root level (fallback)
  const getParticipationPeriod = (): number => {
    return wheelData.configuration?.participationPeriodSeconds || wheelData.participationPeriodSeconds || 86400;
  };

  // Get progress criteria type name based on WheelProgressCriteria enum
  // BetTotalAmount = 1, BetCount = 2
  const getProgressCriteriaName = (criteria: number): string => {
    switch (criteria) {
      case 1: // BetTotalAmount
        return 'bet total amount';
      case 2: // BetCount
        return 'bet count';
      default:
        return 'activity';
    }
  };

  // Get user-friendly description text for the progress criteria
  const getProgressCriteriaDescription = (criteria: number, threshold: number, currency: string): string => {
    switch (criteria) {
      case 1: // BetTotalAmount - total amount of all bets
        return `Make bets totaling ${currency} ${threshold.toLocaleString()}`;
      case 2: // BetCount - number of bets
        return `Place ${threshold} bet${threshold !== 1 ? 's' : ''}`;
      default:
        return `Reach the required threshold`;
    }
  };

  // Calculate available prizes (filter out default/empty prizes)
  const getAvailablePrizes = () => {
    if (!wheelData.prizes || wheelData.prizes.length === 0) {
      return [];
    }
    // Filter out default prizes and prizes with no value
    return wheelData.prizes.filter(prize => {
      // Include prizes with free spins (type 2)
      if (prize.type === 2 && (prize.freeSpinQuantity > 0 || prize.freeSpinAmount > 0)) {
        return true;
      }
      // Exclude default/empty prizes
      return false;
    });
  };

  const availablePrizes = getAvailablePrizes();
  const participationPeriod = formatParticipationPeriod(getParticipationPeriod());
  const configuration = wheelData.configuration;
  const progressCriteria = configuration?.progressCriteria || 1;
  const progressCriteriaName = getProgressCriteriaName(progressCriteria);
  const thresholdValue = configuration?.progressCriteriaThreshold || configuration?.minBetAmount || 0;
  const displayCurrency = configuration?.currency || wheelData.currency;
  const progressDescription = configuration
    ? getProgressCriteriaDescription(progressCriteria, thresholdValue, displayCurrency)
    : 'Make bets or turnover to reach the threshold';

  return (
    <div className={`new-fortune-wheel-modal-overlay ${isClosing ? 'closing' : ''}`}>
      <div className="new-fortune-wheel-modal">
        {/* Close button */}
        <div className="close-btn" onClick={handleClose}>×</div>

        {/* Fortune Wheel Banner */}
        {/* <div className="fortune-wheel-banner">
          <div className="banner-overlay">
            <div className="promo-badge">🎰 FORTUNE WHEEL 🎰</div>
            <div className="promo-title">{wheelData.name}</div>
          </div>
        </div> */}

        {/* Content */}
        <div className="modal-content">
          {/* Promotion Description */}
          <div className="promotion-description">
            <h2 className="promotion-title">Spin & Win</h2>
            <p className="promotion-text">
              {progressDescription} to unlock your fortune wheel spin!
            </p>
          </div>

          {/* How It Works Section */}
          <div className="how-it-works-section">
            <h3 className="section-title">How It Works</h3>
            <div className="steps-list">
              <div className="step-item">
                <div className="step-number">1</div>
                <div className="step-content">
                  <div className="step-title">
                    {progressCriteria === 1 ? 'Make Bets' : progressCriteria === 2 ? 'Place Bets' : 'Make Bets/Turnover'}
                  </div>
                  <div className="step-description">
                    {configuration ? (
                      <>{progressDescription} to unlock your spin</>
                    ) : (
                      <>Play games and reach the required threshold to unlock your spin</>
                    )}
                  </div>
                </div>
              </div>

              <div className="step-item">
                <div className="step-number">2</div>
                <div className="step-content">
                  <div className="step-title">Spin the Wheel</div>
                  <div className="step-description">
                    Once you reach the threshold, spin the wheel and claim your reward
                  </div>
                </div>
              </div>

              <div className="step-item">
                <div className="step-number">3</div>
                <div className="step-content">
                  <div className="step-title">Win Prizes</div>
                  <div className="step-description">
                    Get free bets, free spins, or try again after {participationPeriod}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Available Prizes */}
          {/* {availablePrizes.length > 0 && (
            <div className="prizes-section">
              <h3 className="section-title">Available Prizes</h3>
              <div className="prizes-grid">
                {availablePrizes.slice(0, 3).map((prize, index) => {
                  const hasFreeSpins = prize.freeSpinQuantity > 0;
                  const hasAmount = prize.freeSpinAmount > 0 && prize.currency;
                  
                  return (
                    <div key={prize.id || index} className="prize-item">
                      <div className="prize-icon">🎁</div>
                      <div className="prize-text">
                        <div className="prize-label">
                          {hasFreeSpins ? 'Free Spins' : hasAmount ? 'Free Bet' : 'Prize'}
                        </div>
                        <div className="prize-value">
                          {hasFreeSpins && hasAmount ? (
                            <>
                              {prize.freeSpinQuantity} spins
                              <div style={{ fontSize: '11px', marginTop: '2px', opacity: 0.9 }}>
                                or {prize.currency} {prize.freeSpinAmount}
                              </div>
                            </>
                          ) : hasFreeSpins ? (
                            `${prize.freeSpinQuantity} spins`
                          ) : hasAmount ? (
                            `${prize.currency} ${prize.freeSpinAmount}`
                          ) : (
                            'Try Again'
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {availablePrizes.length > 3 && (
                  <div className="prize-item more-prizes">
                    <div className="prize-icon">🎰</div>
                    <div className="prize-text">
                      <div className="prize-label">And More!</div>
                      <div className="prize-value">+{availablePrizes.length - 3} prizes</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )} */}

          {/* Requirements Info */}
          {configuration && (
            <div className="requirements-info">
              <h3 className="section-title">Requirements</h3>
              <div className="requirements-list">
                {configuration.minBetAmount > 0 && (
                  <div className="requirement-item">
                    <div className="requirement-icon">💰</div>
                    <div className="requirement-text">
                      <div className="requirement-label">Min Bet Amount</div>
                      <div className="requirement-value">{displayCurrency} {configuration.minBetAmount.toLocaleString()}</div>
                    </div>
                  </div>
                )}
                {configuration.progressCriteriaThreshold > 0 && (
                  <div className="requirement-item">
                    <div className="requirement-icon">🎯</div>
                    <div className="requirement-text">
                      <div className="requirement-label">
                        {progressCriteria === 1 ? 'Bet Total Amount' : progressCriteria === 2 ? 'Bet Count' : 'Threshold'} Threshold
                      </div>
                      <div className="requirement-value">
                        {progressCriteria === 1
                          ? `${displayCurrency} ${configuration.progressCriteriaThreshold.toLocaleString()}`
                          : `${configuration.progressCriteriaThreshold} bet${configuration.progressCriteriaThreshold !== 1 ? 's' : ''}`
                        }
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Participation Period Info */}
          <div className="participation-info">
            <div className="info-item">
              <div className="info-icon">⏰</div>
              <div className="info-text">
                <div className="info-label">Daily Reset</div>
                <div className="info-value">Come back every {participationPeriod} to spin again!</div>
              </div>
            </div>
          </div>

          {/* Call-to-Action Buttons */}
          <div className="cta-section">
            <button className="cta-button primary" onClick={handlePlayNow}>
              Continue
            </button>
          </div>

          {/* Don't Show Again Option */}
          <div className="dont-show-again">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
              />
              <span className="checkmark"></span>
              Don't show this notification again
            </label>
          </div>

          {/* Additional Info */}
          <div className="additional-info">
            <p>Start playing now to unlock your fortune wheel spin and win amazing prizes!</p>
          </div>
        </div>
      </div>
    </div>
  );
});